import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../../config';

function Payments() {
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [editAppointmentId, setEditAppointmentId] = useState('');
  const [editClientId, setEditClientId] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editTipAmount, setEditTipAmount] = useState('');
  const [editDiscountAmount, setEditDiscountAmount] = useState('');
  const [formData, setFormData] = useState({
    client_id: '',
    appointment_id: '',
    amount: '',
    tip_amount: '0',
    discount_amount: '0',
    payment_method: 'cash',
    notes: ''
  });
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cvv: '',
    expirationMonth: '',
    expirationYear: '',
    postalCode: ''
  });
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
    fetchClients();
    fetchAppointments();
  }, []);

  // Handle incoming appointment data from navigation
  useEffect(() => {
    if (location.state?.appointmentData && appointments.length > 0) {
      const aptData = location.state.appointmentData;
      const price = aptData.total_price || aptData.service_price || 0;

      setFormData({
        client_id: aptData.client_id.toString(),
        appointment_id: aptData.id.toString(),
        amount: price.toString(),
        tip_amount: '0',
        discount_amount: '0',
        payment_method: 'cash',
        notes: ''
      });
      setShowModal(true);

      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, appointments]);

  const fetchPayments = async () => {
    const response = await axios.get(`${API_URL}/payments`);
    setPayments(response.data);
  };

  const fetchClients = async () => {
    const response = await axios.get(`${API_URL}/clients`);
    setClients(response.data);
  };

  const fetchAppointments = async () => {
    const response = await axios.get(`${API_URL}/appointments`);
    setAppointments(response.data.filter(a => a.status === 'scheduled'));
  };

  const calculateTotal = () => {
    const amount = parseFloat(formData.amount) || 0;
    const tip = parseFloat(formData.tip_amount) || 0;
    const discount = parseFloat(formData.discount_amount) || 0;
    return (amount + tip - discount).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const paymentData = { ...formData };

      // Only add sourceId for card payments
      if (formData.payment_method === 'card') {
        // In a real implementation, you would use Square Web Payments SDK
        // to tokenize the card and get a sourceId
        paymentData.sourceId = 'cnon:card-nonce-ok'; // This would come from Square's card tokenization
      }

      console.log('Submitting payment with data:', paymentData);
      console.log('Auth header:', axios.defaults.headers.common['Authorization']);

      const response = await axios.post(`${API_URL}/payments`, paymentData);

      console.log('Payment response:', response.data);

      setShowModal(false);
      setFormData({
        client_id: '',
        appointment_id: '',
        amount: '',
        tip_amount: '0',
        discount_amount: '0',
        payment_method: 'cash',
        notes: ''
      });
      setCardData({
        cardNumber: '',
        cvv: '',
        expirationMonth: '',
        expirationYear: '',
        postalCode: ''
      });
      fetchPayments();
      alert('Payment processed successfully!');
    } catch (error) {
      console.error('Payment error:', error);
      console.error('Error response:', error.response);

      let errorMessage = 'Payment failed: ';
      if (error.response?.status === 401) {
        errorMessage += 'Unauthorized - Please log in again';
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else {
        errorMessage += error.message;
      }

      alert(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleRefund = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const response = await axios.post(`${API_URL}/payments/${selectedPayment.id}/refund`, {
        amount: refundAmount,
        reason: refundReason
      });

      setShowRefundModal(false);
      setSelectedPayment(null);
      setRefundAmount('');
      setRefundReason('');
      fetchPayments();

      // Show appropriate message based on payment method
      alert(response.data.message || 'Refund processed successfully!');
    } catch (error) {
      alert('Refund failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const openRefundModal = (payment) => {
    setSelectedPayment(payment);
    // Set default refund amount to remaining refundable amount (total - already refunded)
    const totalCharged = parseFloat(payment.amount) + parseFloat(payment.tip_amount || 0);
    const alreadyRefunded = parseFloat(payment.refund_amount || 0);
    const remainingRefundable = totalCharged - alreadyRefunded;
    setRefundAmount(remainingRefundable.toFixed(2));
    setShowRefundModal(true);
  };

  const syncWithSquare = async () => {
    if (!window.confirm('Sync payments from Square? This will import the last 30 days of transactions.')) {
      return;
    }

    setProcessing(true);

    try {
      const response = await axios.post(`${API_URL}/payments/sync`, {});

      const { stats, dateRange } = response.data;

      alert(
        `Sync Complete!\n\n` +
        `Total Square Payments: ${stats.total}\n` +
        `New Payments Imported: ${stats.synced}\n` +
        `Existing Updated: ${stats.updated}\n` +
        `Already in Database: ${stats.skipped}\n\n` +
        `Date Range: ${new Date(dateRange.from).toLocaleDateString()} - ${new Date(dateRange.to).toLocaleDateString()}`
      );

      fetchPayments();
    } catch (error) {
      console.error('Sync error:', error);
      alert('Sync failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { text: 'Completed', class: 'status-completed' },
      pending: { text: 'Pending', class: 'status-pending' },
      failed: { text: 'Failed', class: 'status-cancelled' },
      refunded: { text: 'Refunded', class: 'status-cancelled' },
      partially_refunded: { text: 'Partially Refunded', class: 'status-warning' }
    };
    return badges[status] || { text: status, class: '' };
  };

  const openEditModal = (payment) => {
    setSelectedPayment(payment);
    setEditClientId(payment.client_id || '');
    setEditAppointmentId(payment.appointment_id || '');
    setEditAmount(payment.amount || '');
    setEditTipAmount(payment.tip_amount || '0');
    setEditDiscountAmount(payment.discount_amount || '0');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedPayment(null);
    setEditClientId('');
    setEditAppointmentId('');
    setEditAmount('');
    setEditTipAmount('');
    setEditDiscountAmount('');
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/payments/${selectedPayment.id}`, {
        client_id: editClientId,
        appointment_id: editAppointmentId || null,
        amount: editAmount,
        tip_amount: editTipAmount,
        discount_amount: editDiscountAmount
      });
      alert('Payment updated successfully!');
      fetchPayments();
      closeEditModal();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredPayments = payments
    .filter(payment => {
      if (filter === 'all') return true;
      return payment.status === filter;
    })
    .sort((a, b) => {
      // Sort by service date first (appointment_date) - newest to oldest
      if (a.appointment_date && b.appointment_date) {
        if (a.appointment_date !== b.appointment_date) {
          return b.appointment_date.localeCompare(a.appointment_date); // Most recent service date first
        }
        // If same date, sort by appointment_time - newest to oldest
        if (a.appointment_time && b.appointment_time) {
          if (a.appointment_time !== b.appointment_time) {
            return b.appointment_time.localeCompare(a.appointment_time); // Latest time first
          }
        }
        // If same service date and time, sort by payment date
        return new Date(b.created_at) - new Date(a.created_at);
      }
      // If one has appointment_date and other doesn't, prioritize the one with appointment_date
      if (a.appointment_date && !b.appointment_date) return -1;
      if (!a.appointment_date && b.appointment_date) return 1;

      // Finally, sort by payment date (created_at) - newest to oldest
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const stats = {
    // Total processed = sum of (amount + tip - discount) for all completed/partially_refunded payments
    total: payments
      .filter(p => ['completed', 'partially_refunded', 'refunded'].includes(p.status))
      .reduce((sum, p) => {
        const amount = parseFloat(p.amount || 0);
        const tip = parseFloat(p.tip_amount || 0);
        const discount = parseFloat(p.discount_amount || 0);
        return sum + (amount + tip - discount);
      }, 0),
    // Refunded = sum of all refund amounts (for both fully and partially refunded)
    refunded: payments.reduce((sum, p) => sum + parseFloat(p.refund_amount || 0), 0),
    // Tips = sum of all tips
    tips: payments.reduce((sum, p) => sum + parseFloat(p.tip_amount || 0), 0),
    count: payments.length
  };

  return (
    <div className="payments">
      <div className="header">
        <h2>Payments</h2>
        <div className="header-actions">
          <button onClick={syncWithSquare} className="btn-secondary">Sync with Square</button>
          <button onClick={() => setShowModal(true)}>Process Payment</button>
        </div>
      </div>

      <div className="payment-stats">
        <div className="stat-card">
          <h3>Total Processed</h3>
          <p className="stat-value">{formatCurrency(stats.total)}</p>
          <span className="stat-label">{stats.count} transactions</span>
        </div>
        <div className="stat-card">
          <h3>Tips</h3>
          <p className="stat-value">{formatCurrency(stats.tips)}</p>
        </div>
        <div className="stat-card">
          <h3>Refunded</h3>
          <p className="stat-value">{formatCurrency(stats.refunded)}</p>
        </div>
        <div className="stat-card">
          <h3>Net Revenue</h3>
          <p className="stat-value">{formatCurrency(stats.total - stats.refunded)}</p>
        </div>
      </div>

      <div className="payment-filters">
        <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
        <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Completed</button>
        <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Pending</button>
        <button className={filter === 'partially_refunded' ? 'active' : ''} onClick={() => setFilter('partially_refunded')}>Partially Refunded</button>
        <button className={filter === 'refunded' ? 'active' : ''} onClick={() => setFilter('refunded')}>Refunded</button>
        <button className={filter === 'failed' ? 'active' : ''} onClick={() => setFilter('failed')}>Failed</button>
      </div>

      <table>
        <thead>
          <tr>
            <th>Service Date</th>
            <th>Payment Date</th>
            <th>Client</th>
            <th>Service</th>
            <th>Stylist</th>
            <th>Amount</th>
            <th>Tip</th>
            <th>Discount</th>
            <th>Total</th>
            <th>Method</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredPayments.map(payment => {
            // Handle multiple services display
            let serviceDisplay = payment.service_name || 'Walk-in';
            if (payment.services && payment.services.length > 0) {
              if (payment.services.length === 1) {
                serviceDisplay = payment.services[0].name;
              } else {
                serviceDisplay = payment.services.map(s => s.name).join(', ');
              }
            }

            return (
              <tr key={payment.id}>
                <td>
                  {payment.appointment_date && payment.appointment_time
                    ? `${new Date(payment.appointment_date + 'T00:00:00').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })} ${formatTime(payment.appointment_time)}`
                    : '-'}
                </td>
                <td>{formatDate(payment.created_at)}</td>
                <td>{payment.client_name || <em style={{color: '#999'}}>Not linked</em>}</td>
                <td>{serviceDisplay}</td>
                <td>{payment.stylist_name || '-'}</td>
                <td>{formatCurrency(payment.amount)}</td>
              <td>{formatCurrency(payment.tip_amount)}</td>
              <td>{payment.discount_amount > 0 ? `-${formatCurrency(payment.discount_amount)}` : '-'}</td>
              <td className="total-amount">
                {formatCurrency(parseFloat(payment.amount) + parseFloat(payment.tip_amount) - parseFloat(payment.discount_amount))}
              </td>
              <td className="payment-method">{payment.payment_method || 'Card'}</td>
              <td>
                <span className={`status-badge ${getStatusBadge(payment.status).class}`}>
                  {getStatusBadge(payment.status).text}
                </span>
              </td>
              <td>
                <div className="actions">
                  <button onClick={() => openEditModal(payment)} className="btn-small">Edit</button>
                  {payment.receipt_url && (
                    <>
                      <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="btn-small">Receipt</a>
                    </>
                  )}
                  {payment.status === 'completed' && (
                    <>
                      <button onClick={() => openRefundModal(payment)} className="btn-small warning">Refund</button>
                    </>
                  )}
                  {payment.status === 'partially_refunded' && (
                    <>
                      <span className="refund-badge">Refunded: {formatCurrency(payment.refund_amount)}</span>
                      <button onClick={() => openRefundModal(payment)} className="btn-small warning">Refund More</button>
                    </>
                  )}
                  {payment.status === 'refunded' && payment.refund_amount > 0 && (
                    <>
                      <span className="refund-badge">Refunded: {formatCurrency(payment.refund_amount)}</span>
                    </>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {filteredPayments.length === 0 && (
        <div className="no-data">
          <p>No payments found{filter !== 'all' ? ` with status: ${filter}` : ''}.</p>
        </div>
      )}

      {showModal && (
        <div className="modal">
          <div className="modal-content large">
            <h3>Process Payment</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Client *</label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                    required
                  >
                    <option value="">Select Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name} - {client.phone}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Appointment *</label>
                  <select
                    value={formData.appointment_id}
                    onChange={(e) => {
                      const apt = appointments.find(a => a.id === parseInt(e.target.value));
                      if (apt) {
                        // Use total_price for multi-service appointments, otherwise service_price
                        const price = apt.total_price || apt.service_price || 0;
                        setFormData({
                          ...formData,
                          appointment_id: e.target.value,
                          amount: price
                        });
                      } else {
                        setFormData({
                          ...formData,
                          appointment_id: e.target.value
                        });
                      }
                    }}
                    disabled={!formData.client_id}
                    required
                  >
                    <option value="">
                      {formData.client_id ? 'Select an appointment' : 'Select a client first'}
                    </option>
                    {appointments
                      .filter(apt => !formData.client_id || apt.client_id === parseInt(formData.client_id))
                      .sort((a, b) => {
                        // Sort by date first, then by time
                        if (a.appointment_date !== b.appointment_date) {
                          return a.appointment_date.localeCompare(b.appointment_date);
                        }
                        return a.appointment_time.localeCompare(b.appointment_time);
                      })
                      .map(apt => {
                        // Handle multiple services display
                        let serviceDisplay = apt.service_name;
                        if (apt.services && apt.services.length > 0) {
                          if (apt.services.length === 1) {
                            serviceDisplay = apt.services[0].name;
                          } else {
                            serviceDisplay = apt.services.map(s => s.name).join(', ');
                          }
                        }

                        return (
                          <option key={apt.id} value={apt.id}>
                            {serviceDisplay} - {new Date(apt.appointment_date + 'T00:00:00').toLocaleDateString()} - {formatTime(apt.appointment_time)}
                          </option>
                        );
                      })}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Credit/Debit Card (Square)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tip</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.tip_amount}
                    onChange={(e) => setFormData({...formData, tip_amount: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Discount</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.discount_amount}
                    onChange={(e) => setFormData({...formData, discount_amount: e.target.value})}
                  />
                </div>
              </div>

              <div className="payment-total">
                <strong>Total to Charge: {formatCurrency(calculateTotal())}</strong>
              </div>

              {formData.payment_method === 'card' && (
                <div className="card-fields">
                  <h4>Card Information</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Card Number *</label>
                      <input
                        type="text"
                        placeholder="4111 1111 1111 1111"
                        value={cardData.cardNumber}
                        onChange={(e) => setCardData({...cardData, cardNumber: e.target.value})}
                        required={formData.payment_method === 'card'}
                      />
                    </div>
                    <div className="form-group">
                      <label>CVV *</label>
                      <input
                        type="text"
                        placeholder="123"
                        maxLength="4"
                        value={cardData.cvv}
                        onChange={(e) => setCardData({...cardData, cvv: e.target.value})}
                        required={formData.payment_method === 'card'}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expiration Month *</label>
                      <input
                        type="text"
                        placeholder="MM"
                        maxLength="2"
                        value={cardData.expirationMonth}
                        onChange={(e) => setCardData({...cardData, expirationMonth: e.target.value})}
                        required={formData.payment_method === 'card'}
                      />
                    </div>
                    <div className="form-group">
                      <label>Expiration Year *</label>
                      <input
                        type="text"
                        placeholder="YY"
                        maxLength="2"
                        value={cardData.expirationYear}
                        onChange={(e) => setCardData({...cardData, expirationYear: e.target.value})}
                        required={formData.payment_method === 'card'}
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code *</label>
                      <input
                        type="text"
                        placeholder="12345"
                        value={cardData.postalCode}
                        onChange={(e) => setCardData({...cardData, postalCode: e.target.value})}
                        required={formData.payment_method === 'card'}
                      />
                    </div>
                  </div>
                  <div className="payment-notice">
                    <p><strong>Note:</strong> Square Web Payments SDK integration required for secure card processing.</p>
                    <p>This is a placeholder form. In production, use Square's card tokenization.</p>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  placeholder="Payment notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" disabled={processing}>
                  {processing ? 'Processing...' : 'Process Payment'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} disabled={processing}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRefundModal && selectedPayment && (
        <div className="modal">
          <div className="modal-content">
            <h3>Process Refund</h3>
            <div className="refund-details">
              <p><strong>Client:</strong> {selectedPayment.client_name}</p>
              <p><strong>Payment Amount:</strong> {formatCurrency(selectedPayment.amount)}</p>
              {selectedPayment.tip_amount > 0 && (
                <p><strong>Tip Amount:</strong> {formatCurrency(selectedPayment.tip_amount)}</p>
              )}
              <p><strong>Total Charged:</strong> {formatCurrency(parseFloat(selectedPayment.amount) + parseFloat(selectedPayment.tip_amount || 0))}</p>
              {selectedPayment.refund_amount > 0 && (
                <p><strong>Already Refunded:</strong> {formatCurrency(selectedPayment.refund_amount)}</p>
              )}
              <p><strong>Payment ID:</strong> {selectedPayment.square_payment_id}</p>
            </div>
            <form onSubmit={handleRefund}>
              <div className="form-group">
                <label>Refund Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  max={parseFloat(selectedPayment.amount) + parseFloat(selectedPayment.tip_amount || 0) - parseFloat(selectedPayment.refund_amount || 0)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  required
                />
                <small style={{display: 'block', marginTop: '5px', color: '#666'}}>
                  Max refundable: {formatCurrency(parseFloat(selectedPayment.amount) + parseFloat(selectedPayment.tip_amount || 0) - parseFloat(selectedPayment.refund_amount || 0))}
                </small>
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea
                  placeholder="Reason for refund"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button type="submit" disabled={processing} className="warning">
                  {processing ? 'Processing...' : 'Process Refund'}
                </button>
                <button type="button" onClick={() => setShowRefundModal(false)} disabled={processing}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {showEditModal && selectedPayment && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Payment</h3>
            <div className="payment-details">
              <p><strong>Payment Method:</strong> {selectedPayment.payment_method}</p>
              <p><strong>Created:</strong> {formatDate(selectedPayment.created_at)}</p>
            </div>
            <form onSubmit={handleUpdatePayment}>
              <div className="form-group">
                <label>Client *</label>
                <select
                  value={editClientId}
                  onChange={(e) => {
                    setEditClientId(e.target.value);
                    // Reset appointment when client changes
                    setEditAppointmentId('');
                  }}
                  required
                >
                  <option value="">Select a client</option>
                  {clients
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tip</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editTipAmount}
                    onChange={(e) => setEditTipAmount(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Discount</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editDiscountAmount}
                    onChange={(e) => setEditDiscountAmount(e.target.value)}
                  />
                </div>
              </div>

              <div className="payment-total">
                <strong>Total: {formatCurrency((parseFloat(editAmount) || 0) + (parseFloat(editTipAmount) || 0) - (parseFloat(editDiscountAmount) || 0))}</strong>
              </div>

              <div className="form-group">
                <label>Link to Appointment (Optional)</label>
                <select
                  value={editAppointmentId}
                  onChange={(e) => setEditAppointmentId(e.target.value)}
                >
                  <option value="">None (Keep as walk-in payment)</option>
                  {appointments
                    .filter(apt => {
                      // Only show appointments for the selected client
                      if (!editClientId || apt.client_id !== parseInt(editClientId)) return false;

                      // Show appointments that don't have a payment, OR the appointment currently linked to this payment
                      const hasPayment = payments.some(p => p.appointment_id === apt.id && p.id !== selectedPayment.id);
                      return !hasPayment || apt.id === selectedPayment.appointment_id;
                    })
                    .sort((a, b) => {
                      if (a.appointment_date !== b.appointment_date) {
                        return a.appointment_date.localeCompare(b.appointment_date);
                      }
                      return a.appointment_time.localeCompare(b.appointment_time);
                    })
                    .map(apt => {
                      let serviceDisplay = apt.service_name;
                      if (apt.services && apt.services.length > 0) {
                        if (apt.services.length === 1) {
                          serviceDisplay = apt.services[0].name;
                        } else {
                          serviceDisplay = apt.services.map(s => s.name).join(', ');
                        }
                      }
                      return (
                        <option key={apt.id} value={apt.id}>
                          {serviceDisplay} - {new Date(apt.appointment_date + 'T00:00:00').toLocaleDateString()} - {formatTime(apt.appointment_time)}
                        </option>
                      );
                    })}
                </select>
                <small style={{display: 'block', marginTop: '5px', color: '#666'}}>
                  Select an appointment to link this payment, or leave as "None" for walk-in/Square payments
                </small>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-primary">Update Payment</button>
                <button type="button" onClick={closeEditModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
